import { useEffect, useState } from "react";
import type * as Y from "yjs";
import type { BlockNode } from "./graph-types";
import { useYDoc } from "./yjs-provider";

/**
 * Returns a list of all current Node IDs in the graph.
 */
export function useNodes() {
  const doc = useYDoc();
  const nodesMap = doc.getMap("nodes");

  const [keys, setKeys] = useState<string[]>(Array.from(nodesMap.keys()));

  useEffect(() => {
    const observer = () => setKeys(Array.from(nodesMap.keys()));
    nodesMap.observe(observer);
    return () => nodesMap.unobserve(observer);
  }, [nodesMap]);

  return keys;
}

/**
 * Strongly-typed deep subscription to a single node.
 * Fast panning/dragging won't re-render siblings.
 */
export function useNode<T = any>(id: string): T | null {
  const doc = useYDoc();
  const nodesMap = doc.getMap("nodes");

  const [data, setData] = useState<T | null>(() => {
    const n = nodesMap.get(id) as Y.Map<any> | undefined;
    return n ? (n.toJSON() as T) : null;
  });

  useEffect(() => {
    let nodeMap = nodesMap.get(id) as Y.Map<any> | undefined;

    const handleNodeChange = () => {
      setData((nodeMap?.toJSON() as T) || null);
    };

    const toggleNodeObserver = (
      newNodeMap?: Y.Map<any>,
      oldNodeMap?: Y.Map<any>,
    ) => {
      if (oldNodeMap) oldNodeMap.unobserveDeep(handleNodeChange);
      if (newNodeMap) newNodeMap.observeDeep(handleNodeChange);
    };

    const handleParentChange = (event: Y.YMapEvent<any>) => {
      if (event.keysChanged.has(id)) {
        const _oldMap = nodeMap;
        nodeMap = nodesMap.get(id) as Y.Map<any> | undefined;
        setData(nodeMap ? (nodeMap.toJSON() as T) : null);
        toggleNodeObserver(nodeMap, _oldMap);
      }
    };

    nodesMap.observe(handleParentChange);
    if (nodeMap) toggleNodeObserver(nodeMap);

    return () => {
      nodesMap.unobserve(handleParentChange);
      if (nodeMap) toggleNodeObserver(undefined, nodeMap);
    };
  }, [nodesMap, id]);

  return data;
}

/**
 * Returns a list of all current Edge IDs in the graph.
 */
export function useEdges() {
  const doc = useYDoc();
  const edgesMap = doc.getMap("edges");

  const [keys, setKeys] = useState<string[]>(Array.from(edgesMap.keys()));

  useEffect(() => {
    const observer = () => setKeys(Array.from(edgesMap.keys()));
    edgesMap.observe(observer);
    return () => edgesMap.unobserve(observer);
  }, [edgesMap]);

  return keys;
}

/**
 * Alias for `useNodes` under block-first terminology.
 */
export function useBlocks() {
  return useNodes();
}

/**
 * Alias for `useNode` under block-first terminology.
 */
export function useBlock<T = BlockNode>(id: string) {
  return useNode<T>(id);
}

export interface BlockSummary {
  id: string;
  name?: string;
  type: string;
}

/**
 * Returns light summaries for all blocks for selection/autocomplete UIs.
 */
export function useBlockSummaries(): BlockSummary[] {
  const doc = useYDoc();
  const nodesMap = doc.getMap("nodes");

  const getSummaries = (): BlockSummary[] =>
    Array.from(
      nodesMap.entries() as IterableIterator<[string, Y.Map<unknown>]>,
    ).map(([id, nodeMap]) => {
      const node = nodeMap.toJSON() as
        | { name?: string; type?: string }
        | undefined;

      return {
        id,
        name: node?.name,
        type: node?.type ?? "text",
      };
    });

  const [summaries, setSummaries] = useState<BlockSummary[]>(getSummaries);

  useEffect(() => {
    const observer = () => setSummaries(getSummaries());
    nodesMap.observeDeep(observer);
    return () => nodesMap.unobserveDeep(observer);
  }, [nodesMap, getSummaries]);

  return summaries;
}

export interface HierarchyIndex {
  rootIds: string[];
  childIdsByParent: Record<string, string[]>;
}

export interface RelatedBlockLink {
  edgeId: string;
  relatedBlockId: string;
  direction: "outgoing" | "incoming";
  edgeType: string;
  source: "explicit" | "markdown";
}

function getHierarchyChildren(edgesMap: Y.Map<unknown>, parentId: string) {
  const childIds: string[] = [];

  for (const [, edgeMap] of edgesMap.entries()) {
    const edge = (edgeMap as Y.Map<unknown>).toJSON() as
      | {
          source_id?: string;
          target_id?: string;
          type?: string;
        }
      | undefined;

    if (
      edge?.type === "hierarchy" &&
      edge.source_id === parentId &&
      edge.target_id
    ) {
      childIds.push(edge.target_id);
    }
  }

  return childIds;
}

function getSubtreeIds(edgesMap: Y.Map<unknown>, rootId: string) {
  const result = new Set<string>([rootId]);
  const queue = [rootId];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) continue;

    const children = getHierarchyChildren(edgesMap, current);
    children.forEach((childId) => {
      if (!result.has(childId)) {
        result.add(childId);
        queue.push(childId);
      }
    });
  }

  return result;
}

function getMarkdownReferenceTokens(content: string) {
  const tokens = new Set<string>();

  for (const match of content.matchAll(/\[\[([^\]]+)\]\]/g)) {
    const token = match[1]?.trim();
    if (token) tokens.add(token);
  }

  for (const match of content.matchAll(/\[[^\]]+\]\(block:([^)]+)\)/g)) {
    const token = match[1]?.trim();
    if (token) tokens.add(token);
  }

  for (const match of content.matchAll(/\[[^\]]+\]\(#([^)]+)\)/g)) {
    const token = match[1]?.trim();
    if (token) tokens.add(token);
  }

  return Array.from(tokens);
}

/**
 * Builds a document hierarchy view from blocks + hierarchy edges.
 */
export function useHierarchyIndex(): HierarchyIndex {
  const doc = useYDoc();
  const blockIds = useBlocks();
  const edgeIds = useEdges();

  const nodesMap = doc.getMap("nodes");
  const edgesMap = doc.getMap("edges");

  const childIdsByParent: Record<string, string[]> = {};
  const childOrderById = new Map<string, number>();
  const allChildren = new Set<string>();

  edgeIds.forEach((edgeId) => {
    const edge = (
      edgesMap.get(edgeId) as Y.Map<unknown> | undefined
    )?.toJSON() as
      | {
          source_id?: string;
          target_id?: string;
          type?: string;
          order?: number;
        }
      | undefined;

    if (!edge || edge.type !== "hierarchy") return;
    if (!edge.source_id || !edge.target_id) return;

    if (!childIdsByParent[edge.source_id]) {
      childIdsByParent[edge.source_id] = [];
    }

    childIdsByParent[edge.source_id].push(edge.target_id);
    allChildren.add(edge.target_id);
    if (typeof edge.order === "number") {
      childOrderById.set(edge.target_id, edge.order);
    }
  });

  const sortIds = (ids: string[]) => {
    ids.sort((a, b) => {
      const orderA = childOrderById.get(a) ?? Number.MAX_SAFE_INTEGER;
      const orderB = childOrderById.get(b) ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;

      const nodeA = (nodesMap.get(a) as Y.Map<unknown> | undefined)?.toJSON() as
        | { name?: string }
        | undefined;
      const nodeB = (nodesMap.get(b) as Y.Map<unknown> | undefined)?.toJSON() as
        | { name?: string }
        | undefined;

      return (nodeA?.name ?? a).localeCompare(nodeB?.name ?? b);
    });
  };

  Object.values(childIdsByParent).forEach(sortIds);

  const rootIds = blockIds.filter((id) => !allChildren.has(id));
  sortIds(rootIds);

  return {
    rootIds,
    childIdsByParent,
  };
}

/**
 * Returns non-hierarchy related blocks for a given block.
 */
export function useRelatedBlocks(id: string): RelatedBlockLink[] {
  const doc = useYDoc();
  const blockIds = useBlocks();
  const edgeIds = useEdges();
  const nodesMap = doc.getMap("nodes");
  const edgesMap = doc.getMap("edges");

  if (!id) return [];

  const links: RelatedBlockLink[] = [];
  const dedupe = new Set<string>();
  const subtreeIds = getSubtreeIds(edgesMap, id);

  const blockNameLookup = new Map<string, string>();
  blockIds.forEach((blockId) => {
    const block = (
      nodesMap.get(blockId) as Y.Map<unknown> | undefined
    )?.toJSON() as { name?: string } | undefined;

    if (block?.name) {
      blockNameLookup.set(block.name.trim().toLowerCase(), blockId);
    }
    blockNameLookup.set(blockId.trim().toLowerCase(), blockId);
  });

  edgeIds.forEach((edgeId) => {
    const edge = (
      edgesMap.get(edgeId) as Y.Map<unknown> | undefined
    )?.toJSON() as
      | {
          source_id?: string;
          target_id?: string;
          type?: string;
        }
      | undefined;

    if (!edge?.type || edge.type === "hierarchy") return;

    if (edge.source_id && subtreeIds.has(edge.source_id) && edge.target_id) {
      const key = `${edge.target_id}:${edge.type}:explicit:outgoing`;
      if (dedupe.has(key)) return;
      dedupe.add(key);
      links.push({
        edgeId,
        relatedBlockId: edge.target_id,
        direction: "outgoing",
        edgeType: edge.type,
        source: "explicit",
      });
    }

    if (edge.target_id && subtreeIds.has(edge.target_id) && edge.source_id) {
      const key = `${edge.source_id}:${edge.type}:explicit:incoming`;
      if (dedupe.has(key)) return;
      dedupe.add(key);
      links.push({
        edgeId,
        relatedBlockId: edge.source_id,
        direction: "incoming",
        edgeType: edge.type,
        source: "explicit",
      });
    }
  });

  subtreeIds.forEach((subtreeId) => {
    const block = (
      nodesMap.get(subtreeId) as Y.Map<unknown> | undefined
    )?.toJSON() as { content?: unknown } | undefined;

    if (typeof block?.content !== "string") return;

    getMarkdownReferenceTokens(block.content).forEach((token) => {
      const relatedBlockId = blockNameLookup.get(token.trim().toLowerCase());
      if (!relatedBlockId || subtreeIds.has(relatedBlockId)) return;

      const key = `${relatedBlockId}:markdown:markdown:outgoing`;
      if (dedupe.has(key)) return;
      dedupe.add(key);

      links.push({
        edgeId: `markdown:${subtreeId}:${relatedBlockId}`,
        relatedBlockId,
        direction: "outgoing",
        edgeType: "markdown",
        source: "markdown",
      });
    });
  });

  return links;
}

export type EncounterStatus = "preparing" | "active" | "paused" | "ended";

export interface EncounterTurnEntry {
  blockId: string;
  initiative: number;
  name: string;
}

export interface EncounterState {
  id: string;
  status: EncounterStatus;
  round: number;
  currentTurnIndex: number;
  turnOrder: EncounterTurnEntry[];
}

const ENCOUNTER_STATUS: ReadonlySet<string> = new Set([
  "preparing",
  "active",
  "paused",
  "ended",
]);

function toFiniteNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function normalizeEncounterStatus(value: unknown): EncounterStatus {
  return ENCOUNTER_STATUS.has(String(value))
    ? (value as EncounterStatus)
    : "preparing";
}

function parseEncounterTurnOrder(value: unknown): EncounterTurnEntry[] {
  if (typeof value !== "string" || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        if (typeof entry.blockId !== "string" || !entry.blockId.trim())
          return null;
        const initiative = toFiniteNumber(entry.initiative);

        return {
          blockId: entry.blockId,
          initiative,
          name:
            typeof entry.name === "string" && entry.name.trim()
              ? entry.name
              : entry.blockId,
        } as EncounterTurnEntry;
      })
      .filter((entry): entry is EncounterTurnEntry => entry !== null);
  } catch {
    return [];
  }
}

function toEncounterState(
  encounterId: string,
  raw: Y.Map<unknown> | undefined,
): EncounterState | null {
  if (!raw) return null;
  if (raw.get("type") !== "encounter") return null;

  const properties = (raw.get("properties") as Record<string, unknown>) ?? {};

  const round = Math.max(0, toFiniteNumber(properties.round));
  const currentTurnIndex = Math.max(
    0,
    toFiniteNumber(properties.currentTurnIndex),
  );
  const status = normalizeEncounterStatus(properties.status);
  const turnOrder = parseEncounterTurnOrder(properties.turnOrder);

  return {
    id: encounterId,
    status,
    round,
    currentTurnIndex,
    turnOrder,
  };
}

function readEncounterFromScene(
  nodesMap: Y.Map<unknown>,
  edgesMap: Y.Map<unknown>,
  sceneId: string,
): EncounterState | null {
  const encounterIds: string[] = [];

  for (const [, edgeMap] of edgesMap.entries()) {
    const edge = edgeMap as Y.Map<unknown>;
    if (edge.get("type") !== "hierarchy") continue;
    if (edge.get("source_id") !== sceneId) continue;

    const targetId = edge.get("target_id");
    if (typeof targetId === "string") {
      encounterIds.push(targetId);
    }
  }

  let first: EncounterState | null = null;
  let preparing: EncounterState | null = null;

  for (const encounterId of encounterIds) {
    const node = nodesMap.get(encounterId) as Y.Map<unknown> | undefined;
    const state = toEncounterState(encounterId, node);
    if (!state) continue;

    if (state.status === "active") return state;
    if (state.status === "preparing" && !preparing) {
      preparing = state;
    }
    if (!first) first = state;
  }

  if (preparing) return preparing;
  return first;
}

function readGlobalEncounterState(doc: Y.Doc): EncounterState | null {
  const nodesMap = doc.getMap("nodes");
  const candidates: EncounterState[] = [];

  nodesMap.forEach((raw, id) => {
    const state = toEncounterState(
      id as string,
      raw as Y.Map<unknown> | undefined,
    );
    if (state) candidates.push(state);
  });

  if (candidates.length === 0) return null;

  const active = candidates.find((state) => state.status === "active");
  if (active) return active;

  const preparing = candidates.find((state) => state.status === "preparing");
  if (preparing) return preparing;

  return candidates[0] ?? null;
}

function readEncounterForBlock(
  nodesMap: Y.Map<unknown>,
  edgesMap: Y.Map<unknown>,
  blockId: string,
): EncounterState | null {
  const encounterIds = new Set<string>();

  for (const [, edgeMap] of edgesMap.entries()) {
    const edge = edgeMap as Y.Map<unknown>;
    if (edge.get("type") !== "hierarchy") continue;

    const source = edge.get("source_id");
    const target = edge.get("target_id");
    if (typeof source === "string") encounterIds.add(source);
    if (typeof target === "string") encounterIds.add(target);
  }

  let active: EncounterState | null = null;
  let preparing: EncounterState | null = null;
  let any: EncounterState | null = null;

  for (const encounterId of encounterIds) {
    const encounterNode = nodesMap.get(encounterId) as
      | Y.Map<unknown>
      | undefined;
    const encounter = toEncounterState(encounterId, encounterNode);
    if (!encounter) continue;
    if (!encounter.turnOrder.some((entry) => entry.blockId === blockId))
      continue;

    if (encounter.status === "active") {
      active = encounter;
      break;
    }
    if (encounter.status === "preparing" && !preparing) {
      preparing = encounter;
    }
    if (!any) any = encounter;
  }

  return active ?? preparing ?? any;
}

export function useCurrentSceneId() {
  const blockSummaries = useBlockSummaries();
  const scene = blockSummaries.find((block) => block.type === "location");
  return scene?.id ?? "__document_root__";
}

export function useEncounterForScene(
  sceneId: string | null,
): EncounterState | null {
  const doc = useYDoc();
  const nodesMap = doc.getMap("nodes");
  const edgesMap = doc.getMap("edges");
  const [encounter, setEncounter] = useState<EncounterState | null>(null);

  useEffect(() => {
    const readEncounter = () => {
      if (!sceneId) {
        setEncounter(null);
        return;
      }

      const sceneEncounter = readEncounterFromScene(
        nodesMap,
        edgesMap,
        sceneId,
      );
      if (sceneEncounter) {
        setEncounter(sceneEncounter);
        return;
      }

      setEncounter(readGlobalEncounterState(doc));
    };

    readEncounter();
    const onChange = () => readEncounter();

    nodesMap.observeDeep(onChange);
    edgesMap.observeDeep(onChange);
    return () => {
      nodesMap.unobserveDeep(onChange);
      edgesMap.unobserveDeep(onChange);
    };
  }, [doc, nodesMap, edgesMap, sceneId]);

  return encounter;
}

export function useActiveEncounterState() {
  const doc = useYDoc();
  const [encounter, setEncounter] = useState<EncounterState | null>(null);

  useEffect(() => {
    const nodesMap = doc.getMap("nodes");
    const edgesMap = doc.getMap("edges");
    const readEncounter = () => {
      setEncounter(readGlobalEncounterState(doc));
    };

    readEncounter();
    const onChange = () => readEncounter();

    nodesMap.observeDeep(onChange);
    edgesMap.observeDeep(onChange);
    return () => {
      nodesMap.unobserveDeep(onChange);
      edgesMap.unobserveDeep(onChange);
    };
  }, [doc]);

  return encounter;
}

export function useEncounterForBlock(
  blockId: string | null,
): EncounterState | null {
  const doc = useYDoc();
  const [encounter, setEncounter] = useState<EncounterState | null>(null);

  useEffect(() => {
    const nodesMap = doc.getMap("nodes");
    const edgesMap = doc.getMap("edges");

    const readEncounter = () => {
      if (!blockId) {
        setEncounter(null);
        return;
      }
      setEncounter(readEncounterForBlock(nodesMap, edgesMap, blockId));
    };

    readEncounter();
    const onChange = () => readEncounter();

    nodesMap.observeDeep(onChange);
    edgesMap.observeDeep(onChange);
    return () => {
      nodesMap.unobserveDeep(onChange);
      edgesMap.unobserveDeep(onChange);
    };
  }, [blockId, doc]);

  return encounter;
}
